import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBack, IoHomeOutline, IoCube, IoBagOutline, IoMenuOutline } from "react-icons/io5";

const ViewSingleProduct = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureMsg, setFeatureMsg] = useState("");
  const [showFeatureMsg, setShowFeatureMsg] = useState(false);
  useEffect(() => {
    let fadeTimer;
    if (featureMsg) {
      setShowFeatureMsg(true);
      const timer = setTimeout(() => {
        setShowFeatureMsg(false);
        fadeTimer = setTimeout(() => setFeatureMsg(""), 500); // match fade duration
      }, 2000);
      return () => {
        clearTimeout(timer);
        clearTimeout(fadeTimer);
      };
    }
  }, [featureMsg]);

  useEffect(() => {
    const vendorToken = localStorage.getItem("vendorToken") || "<vendor JWT token>";
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/products/${productId}`, {
          headers: {
            Authorization: `Bearer ${vendorToken}`,
          },
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || "Failed to fetch product details");
        setProduct(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">Loading product details...</div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-red-500">{error}</div>;
  }
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">Product not found.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="fixed top-0 left-0 right-0 z-10 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-gray-900" aria-label="Go back">
            {/* Force icon color and provide fallback SVG if icon fails */}
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <IoArrowBack className="h-6 w-6 text-black" />
              {/* Fallback SVG if IoArrowBack fails to render */}
              <svg className="h-6 w-6" style={{ display: 'none' }} viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </span>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Product</h1>
        </div>
      </header>

      <main className="px-6 pt-20 pb-24">
        {featureMsg && (
          <div
            className={`fixed left-1/2 top-4 z-50 w-[90vw] max-w-md -translate-x-1/2 rounded bg-yellow-100 px-4 py-2 text-center text-yellow-800 font-semibold shadow transition-opacity duration-500 ${showFeatureMsg ? 'opacity-100' : 'opacity-0'}`}
            style={{ pointerEvents: 'none' }}
          >
            {featureMsg}
          </div>
        )}
        <div className="mx-auto max-w-md">
          <div className="space-y-4">
            <img
              loading="lazy"
              src={product.productImages && product.productImages[0] ? product.productImages[0] : ''}
              alt={product.productName}
              className="w-full rounded-lg object-cover"
            />

            {/* Product Main Details */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-gray-900">Product Details</h2>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Name:</span>
                  <span className="text-sm text-gray-900">{product.productName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Description:</span>
                  <span className="text-sm text-right text-gray-900">{product.productDescription}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Price:</span>
                  <span className="text-sm font-bold text-gray-900">₦{Number(product.productSellingPrice || 0).toLocaleString("en-NG")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Category:</span>
                  <span className="text-sm text-gray-900">{product.productCategory}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Quantity:</span>
                  <span className="text-sm text-gray-900">{product.productQuantity}</span>
                </div>
              </div>
            </div>

            {/* Product Attributes */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-gray-900">Product Attributes</h2>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Color:</span>
                  <span className="text-sm text-gray-900">{product.productColor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Size:</span>
                  <span className="text-sm text-gray-900">{product.productSize}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Weight:</span>
                  <span className="text-sm text-gray-900">{product.productWeight}</span>
                </div>
              </div>
            </div>

            {/* Vendor Info */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-gray-900">Vendor Information</h2>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Vendor Store Name:</span>
                  <span className="text-sm text-gray-900">{product.vendorStoreName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Vendor Store Location:</span>
                  <span className="text-sm text-gray-900">{product.vendorStoreAddress}</span>
                </div>
              </div>
            </div>

            {/* Product Status and ID */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product Status:</span>
                  <span className="text-sm text-gray-900">{product.status ? product.status.charAt(0).toUpperCase() + product.status.slice(1) : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Product ID:</span>
                  <span className="text-sm text-gray-900">#{product.productCode}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <button
              className="w-full rounded-lg bg-[#19C463] py-3 text-sm font-semibold text-white"
              onClick={() => setFeatureMsg("Feature Coming Soon ðŸ˜‰")}
            >
              Edit Product
            </button>
            <button
              className="w-full rounded-lg bg-[#FE0000] py-3 text-sm font-semibold text-white"
              onClick={() => setFeatureMsg("Feature Coming Soon ðŸ˜‰")}
            >
              Delete Product
            </button>
          </div>
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 flex items-center justify-around border-t border-gray-200 bg-white py-3">
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-home")}>
          <IoHomeOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Home</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/products")}>
          <IoCube className="h-6 w-6 text-[#19C463]" />
          <span className="text-xs font-bold text-[#19C463]">Product</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-orders")}>
          <IoBagOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Orders</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/menu")}>
          <IoMenuOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Menu</span>
        </button>
      </nav>
    </div>
  );
};

export default ViewSingleProduct;


